<template>
  <div v-if="state == State.Initial">
    {{ t('common.loading') }}
  </div>

  <div v-else-if="state == State.EnterRecoveryKey" @drop.prevent="" @dragover.prevent="">
    <BreadcrumbNav :crumbs="[ { label: t('vaultList.title'), to: '/app/vaults' }, { label: t('createVault.enterRecoveryKey.title') } ]" />
    <form ref="form" novalidate @submit.prevent="validateRecoveryKey()">
      <div class="flex justify-center">
        <div class="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6 text-center sm:w-full sm:max-w-lg">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100">
            <ArrowPathIcon class="h-6 w-6 text-emerald-600" aria-hidden="true" />
          </div>
          <div class="mt-3 sm:mt-5">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              {{ t('createVault.enterRecoveryKey.title') }}
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">
                {{ t('createVault.enterRecoveryKey.description') }}
              </p>
            </div>
          </div>
          <!-- Textarea -->
          <div class="mt-5 sm:mt-6">
            <label for="recoveryKey" class="block text-sm font-medium leading-6 text-gray-900">{{ t('createVault.enterRecoveryKey.recoveryKey') }}</label>
            <textarea
              id="recoveryKey"
              v-model="recoveryKeyStr"
              rows="6"
              name="recoveryKey"
              class="block w-full rounded-md border-gray-300 shadow-xs focus:border-primary focus:ring-primary sm:text-sm"
              :class="{ 'invalid:border-red-300 invalid:text-red-900 focus:invalid:ring-red-500 focus:invalid:border-red-500': onRecoverError instanceof FormValidationFailedError }"
              required
            />
          </div>
          <!-- Dropzone -->
          <div class="mt-5 sm:mt-6">
            <label for="metadata-file" class="block text-sm font-medium leading-6 text-gray-900">{{ t('createVault.enterRecoveryKey.vaultMetadataFile') }}</label>
            <div
              class="relative mt-2 flex justify-center rounded-lg border-2  px-6 py-10 border-gray-300 hover:border-gray-400 active:border-primary focus-within:border-primary focus-within:ring-primary  focus-within:ring-offset-2"
              :class="{ 'border-primary': isDraggingOver, 'border-dashed': (vaultMetadata?.length ?? 0) == 0, 'border-red-300 active:border-red-500 focus-within:ring-red-500 focus-within:border-red-500': (vaultMetadata?.length ?? 0) == 0 && onRecoverError instanceof FormValidationFailedError}"
              @dragenter.prevent="event => handleDragEnterAndOver(event)"
              @dragover.prevent="event => handleDragEnterAndOver(event)"
              @dragleave="handleDragLeave()"
              @drop.prevent="event => handleDrop(event)"
            >
              <input id="metadata-file" ref="fileUpload" name="metadata-file" type="file" class="cursor-pointer absolute inset-0 opacity-0" accept=".cryptomator, .uvf" @change="event => handleUpload(event)" />
              <div v-if="(vaultMetadata?.length ?? 0) == 0" class="text-center">
                <ArrowUpOnSquareIcon class="mx-auto h-12 w-12 text-gray-300" aria-hidden="true" />
                <p class="mt-2 block text-sm font-semibold text-gray-900">
                  {{ t('createVault.enterRecoveryKey.uploadCaption') }}
                </p>
                <p class="text-xs leading-5 text-gray-600">{{ t('createVault.enterRecoveryKey.uploadSubcaption') }}</p>
              </div>
              <div v-else class="text-center">
                <DocumentCheckIcon class="mx-auto h-12 w-12 text-primary" aria-hidden="true" />
                <p class="mt-2 block text-sm font-semibold text-gray-900">
                  {{ t('createVault.enterRecoveryKey.uploadSuccessCaption', [metadataFilename]) }}
                </p>
                <p class="text-xs leading-5 text-gray-600">{{ t('createVault.enterRecoveryKey.uploadSuccessSubcaption') }}</p>
              </div>
            </div>
            <div v-if="onUploadError" class="rounded-md bg-red-50 p-4">
              <div class="flex">
                <div class="flex-shrink-0">
                  <XCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true" />
                </div>
                <div class="ml-3 flex-1 md:flex md:justify-between">
                  <p v-if="onUploadError instanceof FileTooBigError" class="text-sm text-red-700">{{ t('createVault.error.uploadTooBig') }}</p>
                  <p v-else-if="onUploadError instanceof WrongFileNameError" class="text-sm text-red-700">{{ t('createVault.error.wrongFileName') }}</p>
                  <p v-else class="text-sm text-red-700">{{ t('createVault.error.failedUpload') }}</p>
                </div>
              </div>
            </div>
          </div>
          <!-- Button -->
          <div class="mt-5 sm:mt-6">
            <button
              type="submit"
              :disabled="processing"
              class="inline-flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-xs hover:bg-primary-d1 focus:outline-hidden focus:ring-2 focus:bg-primary focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:hover:bg-primary disabled:cursor-not-allowed"
            >
              {{ t('createVault.enterRecoveryKey.submit') }}
            </button>
            <div v-if="onRecoverError">
              <p v-if="onRecoverError instanceof FormValidationFailedError" class="text-sm text-red-900 mt-2">{{ t('createVault.error.formValidationFailed') }}</p>
              <p v-else-if="onRecoverError instanceof DecodeUvfRecoveryKeyError || onRecoverError instanceof DecodeVf8RecoveryKeyError" class="text-sm text-red-900 mt-2">{{ t('createVault.error.invalidRecoveryKey') }}</p>
              <p v-else class="text-sm text-red-900 mt-2">{{ t('createVault.error.invalidRecoveryKey') }}</p>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div v-else-if="state == State.EnterVaultDetails">
    <BreadcrumbNav :crumbs="[ { label: t('vaultList.title'), to: '/app/vaults' }, { label: t('createVault.enterVaultDetails.title') } ]" />
    <VaultCreationProgress :state="State.EnterVaultDetails" :steps="getCurrentStates" class="flex justify-center mb-4" />
    <form ref="form" class="space-y-6" novalidate @submit.prevent="validateVaultDetails()">
      <div class="flex justify-center text-center">
        <div class="bg-white shadow-sm rounded-lg overflow-hidden sm:w-full sm:max-w-lg">
          <div class="mx-auto mt-5 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100">
            <PlusIcon class="h-6 w-6 text-emerald-600" aria-hidden="true" />
          </div>
          <div class="mt-3 sm:mt-5 px-4 text-center">
            <h3 class="text-lg font-medium leading-6 text-gray-900">
              {{ t('createVault.enterVaultDetails.title') }}
            </h3>
            <p class="mt-2 text-sm text-gray-500">
              {{ t('createVault.enterVaultDetails.description') }}
            </p>
          </div>

          <div class="mt-6 px-4 space-y-6">
            <div>
              <label for="vaultName" class="block text-sm font-medium text-gray-700 text-left">{{ t('createVault.enterVaultDetails.vaultName') }}</label>
              <input id="vaultName" v-model="vault.name" :disabled="processing" type="text" class="mt-1 focus:ring-primary focus:border-primary block w-full shadow-xs sm:text-sm border-gray-300 rounded-md disabled:bg-gray-200" :class="{ 'invalid:border-red-300 invalid:text-red-900 focus:invalid:ring-red-500 focus:invalid:border-red-500': onCreateError instanceof FormValidationFailedError }" pattern="^(?! )([^\\\/:*?&quot;<>\|])+(?<![ \.])$" required />
              <p v-if="(onCreateError instanceof FormValidationFailedError)" class="text-sm text-red-900 text-left mt-2">
                {{ t('createVault.error.illegalVaultName') }} \, /, :, *, ?, ", &lt;, &gt;, |
              </p>
            </div>

            <div>
              <label for="vaultDescription" class="block text-sm font-medium text-gray-700 text-left">
                {{ t('createVault.enterVaultDetails.vaultDescription') }}
                <span class="text-xs text-gray-500">({{ t('common.optional') }})</span>
              </label>
              <input id="vaultDescription" v-model="vault.description" :disabled="processing" type="text" class="mt-1 focus:ring-primary focus:border-primary block w-full shadow-xs sm:text-sm border-gray-300 rounded-md disabled:bg-gray-200" :class="{ 'invalid:border-red-300 invalid:text-red-900 focus:invalid:ring-red-500 focus:invalid:border-red-500': onCreateError instanceof FormValidationFailedError }" pattern="[^*<>&quot;]*" />
              <p v-if="(onCreateError instanceof FormValidationFailedError)" class="text-sm text-red-900 text-left mt-2">
                {{ t('createVault.error.illegalVaultDescription') }} *, &lt;, &gt;, "
              </p>
            </div>
          </div>

          <div class="bg-gray-50 mt-4 px-4 py-3 sm:px-6">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center sm:space-x-4">
              <div class="text-sm text-red-900 text-right sm:flex-1 sm:min-w-0">
                <template v-if="onCreateError">
                  <p v-if="(onCreateError instanceof FormValidationFailedError)">
                    {{ t('createVault.error.formValidationFailed','') }} 
                  </p>
                  <p v-else-if="(onCreateError instanceof DecodeUvfRecoveryKeyError || onCreateError instanceof DecodeVf8RecoveryKeyError)">
                    {{ t('createVault.error.invalidRecoveryKey','') }} 
                  </p>
                  <p v-else>
                    {{ t('common.unexpectedError', [onCreateError.message]) }}
                  </p>
                </template>
              </div>
              <div class="flex flex-col-reverse sm:flex-row-reverse sm:space-x-reverse sm:space-x-3 flex-shrink-0 mt-4 sm:mt-0">
                <button
                  type="submit"
                  :disabled="processing"
                  class="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-d1 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm disabled:opacity-50 disabled:hover:bg-primary disabled:cursor-not-allowed"
                >
                  {{ t('common.next') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div v-else-if="state == State.DefineAutomaticAccessGrant">
    <BreadcrumbNav :crumbs="[ { label: t('vaultList.title'), to: '/app/vaults' }, { label: t('createVault.enterVaultDetails.title') } ]" />
    <VaultCreationProgress :state="state" :steps="getCurrentStates" class="flex justify-center mb-4" />
    <form @submit.prevent="validateAutomaticAccessGrant()">
      <div class="flex justify-center">
        <div class="bg-white shadow-sm rounded-lg sm:w-full sm:max-w-lg">
          <div class="mx-auto mt-5 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100">
            <UserPlusIcon class="h-6 w-6 text-emerald-600" aria-hidden="true" />
          </div>
          <div class="mt-3 mb-3 px-4 sm:mt-5">
            <h3 class="text-lg leading-6 font-medium text-gray-900 text-center">
              {{ t('createVault.automaticAccessGrant.title') }}
            </h3>
            <p class="mt-2 text-sm text-gray-500 text-center">
              {{ t('createVault.automaticAccessGrant.description') }}
            </p>
            <div class="mt-5 space-y-4 text-left">
              <div class="flex items-center">
                <input id="vaultAutoGrantEnabled" v-model="vaultAutoGrantEnabled" :disabled="processing" type="checkbox" class="h-4 w-4 rounded-sm border-gray-300 text-primary focus:ring-primary" />
                <label for="vaultAutoGrantEnabled" class="ml-2 block text-sm text-gray-700">{{ t('createVault.automaticAccessGrant.enabled.label') }}</label>
              </div>
              <div v-if="vaultAutoGrantEnabled">
                <label for="vaultAutoGrantTrustThreshold" class="block text-sm font-medium text-gray-700">{{ t('createVault.automaticAccessGrant.trustThreshold.label') }}</label>
                <input id="vaultAutoGrantTrustThreshold" v-model="vaultAutoGrantTrustThreshold" :disabled="processing" type="number" min="0" max="9" step="1" class="mt-1 focus:ring-primary focus:border-primary block w-full shadow-xs sm:text-sm border-gray-300 rounded-md disabled:bg-gray-200" />
                <p class="mt-1 text-xs text-gray-500">{{ t('createVault.automaticAccessGrant.trustThreshold.help') }}</p>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 mt-4 px-4 py-3 sm:px-6 rounded-b-lg">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center sm:space-x-4">
              <div class="text-sm text-red-900 sm:flex-1 sm:min-w-0">
                <template v-if="onCreateError">
                  <p>{{ t('common.unexpectedError', [onCreateError.message]) }}</p>
                </template>
              </div>
              <div class="flex flex-col-reverse sm:flex-row-reverse sm:space-x-reverse sm:space-x-3 shrink-0 mt-4 sm:mt-0">
                <button
                  type="submit"
                  :disabled="processing"
                  class="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-d1 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm disabled:opacity-50 disabled:hover:bg-primary disabled:cursor-not-allowed"
                >
                  {{ t('common.next') }}
                </button>
                <button
                  type="button"
                  class="mt-3 sm:mt-0 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm"
                  @click="goToPreviousState()"
                >
                  {{ t('common.previous') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div v-else-if="state == State.DefineEmergencyAccess">
    <BreadcrumbNav :crumbs="[ { label: t('vaultList.title'), to: '/app/vaults' }, { label: t('createVault.enterVaultDetails.title') } ]" />
    <VaultCreationProgress :state="state" :steps="getCurrentStates" class="flex justify-center mb-4" />
    <form @submit.prevent="validateVaultEmergencyAccess()">
      <div class="flex justify-center">
        <div class="bg-white shadow-sm rounded-lg sm:w-full sm:max-w-lg">
          <div class="mx-auto mt-5 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100">
            <ArrowPathIcon class="h-6 w-6 text-emerald-600" aria-hidden="true" />
          </div>
          <div class="mt-3 mb-3 px-4 sm:mt-5">
            <h3 class="text-lg leading-6 font-medium text-gray-900 text-center">
              {{ t('createVault.emergencyAccessDetails.title') }}
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500 text-center">
                {{ settings?.allowChoosingEmergencyCouncil
                  ? t('createVault.emergencyAccessDetails.description')
                  : t('createVault.emergencyAccessDetails.description.adminDefined') }}
              </p>
            </div>
            <EmergencyAccessSetup v-if="settings" ref="emergencyAccessSetup" :settings="settings" :required-key-shares="settings.defaultRequiredEmergencyKeyShares" :allow-choosing-council="settings.allowChoosingEmergencyCouncil" />
          </div>
          <div class="bg-gray-50 mt-4 px-4 py-3 sm:px-6 rounded-b-lg">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center sm:space-x-4">
              <div class="text-sm text-red-900 sm:flex-1 sm:min-w-0">
                <template v-if="onCreateError">
                  <p v-if="!(onCreateError instanceof PaymentRequiredError)">
                    {{ t('common.unexpectedError', [onCreateError.message]) }}
                  </p>
                </template>
              </div>
              <div class="flex flex-col-reverse sm:flex-row-reverse sm:space-x-reverse sm:space-x-3 shrink-0 mt-4 sm:mt-0">
                <button
                  type="submit"
                  :disabled="!emergencyAccessSetup || emergencyAccessSetup.hasValidationErrors || processing"
                  class="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-d1 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm disabled:opacity-50 disabled:hover:bg-primary disabled:cursor-not-allowed"
                >
                  {{ t('common.next') }}
                </button>
                <button
                  type="button"
                  class="mt-3 sm:mt-0 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm"
                  @click="goToPreviousState()"
                >
                  {{ t('common.previous') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div v-else-if="state == State.ShowRecoveryKey">
    <BreadcrumbNav :crumbs="[ { label: t('vaultList.title'), to: '/app/vaults' }, { label: t('createVault.enterVaultDetails.title') } ]" />
    <VaultCreationProgress :state="state" :steps="getCurrentStates" class="flex justify-center mb-4" />
    <form @submit.prevent="createVault()">
      <div class="flex justify-center text-center">
        <div class="bg-white shadow-sm rounded-lg overflow-hidden sm:max-w-lg">
          <div class="mx-auto mt-5 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100">
            <KeyIcon class="h-6 w-6 text-emerald-600" aria-hidden="true" />
          </div>
          <div class="mt-3 sm:mt-5 px-4">
            <h3 class="text-lg leading-6 font-medium text-gray-900">
              {{ t('createVault.showRecoveryKey.title') }}
            </h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">
                {{ t('createVault.showRecoveryKey.description') }}
              </p>
            </div>
            <div class="relative mt-5 sm:mt-6">
              <div class="overflow-hidden rounded-lg border border-gray-300 shadow-xs focus-within:border-primary focus-within:ring-1 focus-within:ring-primary">
                <label for="recoveryKey" class="sr-only">{{ t('createVault.showRecoveryKey.recoveryKey') }}</label>
                <textarea
                  id="recoveryKey"
                  v-model="recoveryKeyStr"
                  rows="6"
                  name="recoveryKey"
                  class="block w-full resize-none border-0 py-3 focus:ring-0 sm:text-sm"
                  readonly
                />
                <!-- Spacer element to match the height of the toolbar -->
                <div class="py-2" aria-hidden="true">
                  <div class="h-9" />
                </div>
              </div>

              <div class="absolute inset-x-0 bottom-0">
                <div class="flex flex-nowrap justify-end space-x-2 py-2 px-2 sm:px-3">
                  <div class="flex-shrink-0">
                    <button
                      type="button"
                      class="relative inline-flex items-center whitespace-nowrap rounded-full bg-gray-50 py-2 px-2 text-sm font-medium text-gray-500 hover:bg-gray-100 sm:px-3"
                      @click="copyRecoveryKey()"
                    >
                      <ClipboardIcon class="h-5 w-5 shrink-0 text-gray-300 sm:-ml-1" aria-hidden="true" />
                      <span v-if="!copiedRecoveryKey" class="hidden truncate sm:ml-2 sm:block text-gray-900">{{ t('common.copy') }}</span>
                      <span v-else class="hidden truncate sm:ml-2 sm:block text-gray-900">{{ t('common.copied') }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="relative flex items-start text-left mt-5 sm:mt-6">
              <div class="flex h-5 items-center">
                <input id="confirmRecoveryKey" v-model="confirmRecoveryKey" name="confirmRecoveryKey" type="checkbox" class="h-4 w-4 rounded-sm border-gray-300 text-primary focus:ring-primary" required />
              </div>
              <div class="ml-3 text-sm">
                <label for="confirmRecoveryKey" class="font-medium text-gray-700">{{ t('createVault.showRecoveryKey.confirmRecoveryKey') }}</label>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 mt-4 px-4 py-3 sm:px-6">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center sm:space-x-4">
              <div class="text-sm text-red-900 sm:flex-1 sm:min-w-0">
                <template v-if="onCreateError">
                  <p v-if="!(onCreateError instanceof PaymentRequiredError)">
                    {{ t('common.unexpectedError', [onCreateError.message]) }}
                  </p>
                  <p v-else>
                    {{ t('createVault.error.paymentRequired') }}
                  </p>
                </template>
              </div>
              <div class="flex flex-col-reverse sm:flex-row-reverse sm:space-x-reverse sm:space-x-3 flex-shrink-0 mt-4 sm:mt-0">
                <button
                  type="submit"
                  :disabled="!confirmRecoveryKey || processing"
                  class="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-d1 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm disabled:opacity-50 disabled:hover:bg-primary disabled:cursor-not-allowed"
                >
                  {{ t('createVault.showRecoveryKey.submit') }}
                </button>
                <button
                  type="button"
                  class="mt-3 sm:mt-0 inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:text-sm"
                  @click="goToPreviousState()"
                >
                  {{ t('common.previous') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div v-else-if="state == State.Finished">
    <BreadcrumbNav :crumbs="[ { label: t('vaultList.title'), to: '/app/vaults' }, { label: t('createVault.enterVaultDetails.title') } ]" />
    <VaultCreationProgress :state="state" :steps="getCurrentStates" class="flex justify-center mb-4" />
    <div class="flex justify-center">
      <div class="bg-white px-4 py-5 shadow-sm sm:rounded-lg sm:p-6 text-center sm:w-full sm:max-w-lg">
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100">
          <CheckIcon class="h-6 w-6 text-emerald-600" aria-hidden="true" />
        </div>
        <div class="mt-3 sm:mt-5">
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            {{ t('createVault.success.title') }}
          </h3>
          <div class="mt-2">
            <p class="text-sm text-gray-500">
              {{ t('createVault.success.description') }}
            </p>
          </div>
        </div>
        <div class="mt-5 sm:mt-6">
          <button
            type="button"
            class="inline-flex items-center px-4 py-2 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-d1 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            @click="downloadVaultTemplate()"
          >
            <ArrowDownTrayIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            {{ t('createVault.success.download') }}
          </button>
          <p v-if="onDownloadTemplateError" class="text-sm text-red-900 mr-4">
            {{ t('createVault.error.downloadTemplateFailed', [onDownloadTemplateError.message]) }}
          </p>
          <!-- TODO: not beautiful-->
        </div>
        <div class="mt-2">
          <router-link to="/app/vaults" class="text-sm text-gray-500">
            {{ t('createVault.success.return') }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ClipboardIcon, XCircleIcon, ArrowDownTrayIcon } from '@heroicons/vue/20/solid';
import { ArrowPathIcon, ArrowUpOnSquareIcon, CheckIcon, DocumentCheckIcon, KeyIcon, PlusIcon, UserPlusIcon } from '@heroicons/vue/24/outline';
import { saveAs } from 'file-saver';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import backend, { AccessGrant, LicenseUserInfoDto, PaymentRequiredError, SettingsDto, VaultDto } from '../common/backend';
import { absBackendBaseURL } from '../common/config';
import { RecoveryKeyProducing, VaultTemplateProducing } from '../common/crypto';
import { DecodeUvfRecoveryKeyError, UniversalVaultFormat } from '../common/universalVaultFormat';
import userdata from '../common/userdata';
import { debounce } from '../common/util';
import BreadcrumbNav from './BreadcrumbNav.vue';
import EmergencyAccessSetup from './emergencyaccess/EmergencyAccessSetup.vue';
import VaultCreationProgress from './VaultCreationProgress.vue';
import { DecodeVf8RecoveryKeyError, VaultFormat8 } from '../common/vaultFormat8';

enum State {
  Initial,
  EnterRecoveryKey,
  EnterVaultDetails,
  DefineAutomaticAccessGrant,
  DefineEmergencyAccess,
  ShowRecoveryKey,
  Finished
}

enum VaultType {
  VaultFormat8,
  UniversalVaultFormat
}

const vaultType = ref(VaultType.VaultFormat8);

class FormValidationFailedError extends Error {

  constructor() {
    super('The form is invalid.');
  }

}

class EmptyVaultTemplateError extends Error {

  constructor() {
    super('Vault template is empty.');
  }

}

class NoFileError extends Error {

  constructor() {
    super('Drag and drop operation has no file.');
  }

}

class WrongFileNameError extends Error {

  constructor() {
    super('Dropped file is not named "vault.cryptomator" or "vault.uvf"');
  }

}

class FileTooBigError extends Error {

  constructor() {
    super('Dropped file exceeds size limit of 8KB');
  }

}

const { t } = useI18n({ useScope: 'global' });

const form = ref<HTMLFormElement>();
const fileUpload = ref<HTMLInputElement>();

const onCreateError = ref<Error>();
const onRecoverError = ref<Error>();
const onDownloadTemplateError = ref<Error>();
const onUploadError = ref<Error>();

const state = ref(State.Initial);
const processing = ref(false);
const settings = ref<SettingsDto>();
const vault = ref<VaultDto>({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  archived: false,
  creationTime: new Date(),
  requiredEmergencyKeyShares: 0,
  emergencyKeyShares: {}
});
// Per-vault override of the global "Automatic Access Grant" defaults; these values are applied when building the vault metadata payload.
const vaultAutoGrantEnabled = ref<boolean>(false);
const vaultAutoGrantTrustThreshold = ref<number>(0);
const copiedRecoveryKey = ref(false);
const debouncedCopyFinish = debounce(() => copiedRecoveryKey.value = false, 2000);
const confirmRecoveryKey = ref(false);
const vaultFormat8 = ref<VaultFormat8>();
const recoveryKeyStr = ref<string>('');
const uvfVault = ref<UniversalVaultFormat>();
const emergencyAccessSetup = ref<InstanceType<typeof EmergencyAccessSetup>>();

const metadataFilename = computed(() => vaultType.value == VaultType.VaultFormat8 ? 'vault.cryptomator' : 'vault.uvf');
const vaultMetadata = ref<string>('');
const isDraggingOver = ref<boolean>(false);

const props = defineProps<{
  recover: boolean,
  uvf?: boolean // create a Universal Vault Format vault instead of the default Vault Format 8
}>();

onMounted(initialize);
const licenseStatus = ref<LicenseUserInfoDto>();

const isCommunityLicense = computed(() => {
  return !licenseStatus.value?.expiresAt;
});

async function initialize() {
  if (props.recover) {
    state.value = State.EnterRecoveryKey;
  } else {
    settings.value = await backend.settings.get();
    vaultType.value = props.uvf ? VaultType.UniversalVaultFormat : VaultType.VaultFormat8;
    switch (vaultType.value) {
      case VaultType.VaultFormat8:
        vaultFormat8.value = await VaultFormat8.create();
        recoveryKeyStr.value = await vaultFormat8.value.createRecoveryKey();
        break;
      case VaultType.UniversalVaultFormat:
        uvfVault.value = await UniversalVaultFormat.create({ enabled: settings.value.enableAutomaticAccessGrant, trustThreshold: settings.value.automaticAccessGrantTrustThreshold });
        recoveryKeyStr.value = await uvfVault.value.recoveryKey.createRecoveryKey();
        break;
    }
    vaultAutoGrantEnabled.value = settings.value.enableAutomaticAccessGrant;
    vaultAutoGrantTrustThreshold.value = settings.value.automaticAccessGrantTrustThreshold;
    state.value = State.EnterVaultDetails;
  }
  licenseStatus.value = await backend.license.getUserInfo();
}

async function handleDragEnterAndOver (event: DragEvent){
  isDraggingOver.value = true;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.dropEffect = 'copy';
  }
}

async function handleDragLeave() {
  isDraggingOver.value = false;
}

async function handleDrop(event: DragEvent) {
  onUploadError.value = undefined;
  isDraggingOver.value = false;
  let file: File | undefined;
  if (event.dataTransfer?.items && event.dataTransfer.items.length >= 1) {
    //new DataTransferItemList API
    const item = event.dataTransfer.items[0];
    if (item.kind == 'file') {
      file = item.getAsFile() ?? undefined;
    }
  } else {
    file = event.dataTransfer?.files[0];
  }
  validateAndSetMetadataFile(file);
}

async function handleUpload(event: Event) {
  onUploadError.value = undefined;
  validateAndSetMetadataFile(fileUpload.value?.files?.item(0) ?? undefined);
}

async function validateAndSetMetadataFile(file: File | undefined) {
  try {
    if (!file) {
      throw new NoFileError();
    } else if (!/^vault\.(cryptomator|uvf)$/.test(file.name)) {
      throw new WrongFileNameError();
    } else if (file.size > 8000) {
      throw new FileTooBigError();
    }

    vaultType.value = file.name.endsWith('.uvf') ? VaultType.UniversalVaultFormat : VaultType.VaultFormat8;
    vaultMetadata.value = await file.text();
  } catch (error) {
    vaultMetadata.value = '';
    onUploadError.value = error instanceof Error ? error : new Error('Error reading file as UTF-8 encoded text.');
  }
}

async function validateRecoveryKey() {
  onRecoverError.value = undefined;
  if (!form.value?.checkValidity() || !vaultMetadata.value ) {
    onRecoverError.value = new FormValidationFailedError();
    return;
  }
  await recoverVault();
}

const autoGrantOverrideAvailable = computed(() => !!settings.value?.enableAutomaticAccessGrant && !!settings.value?.allowAutomaticAccessGrantOverride);
const emergencyAccessAvailable = computed(() => !isCommunityLicense.value && !!settings.value?.enableEmergencyAccess);

const getCurrentStates = computed(() => {
  const steps: State[] = [State.EnterVaultDetails];
  if (autoGrantOverrideAvailable.value) steps.push(State.DefineAutomaticAccessGrant);
  if (emergencyAccessAvailable.value) steps.push(State.DefineEmergencyAccess);
  steps.push(State.ShowRecoveryKey, State.Finished);
  return steps;
});

async function recoverVault() {
  onRecoverError.value = undefined;
  try {
    processing.value = true;
    if (vaultType.value == VaultType.UniversalVaultFormat) {
      uvfVault.value = await UniversalVaultFormat.recover(vaultMetadata.value, recoveryKeyStr.value);
    } else {
      vaultFormat8.value = await VaultFormat8.recoverAndVerify(vaultMetadata.value, recoveryKeyStr.value);
    }
    state.value = State.EnterVaultDetails;
  } catch (error) {
    console.error('Recovering vault failed.', error);
    onRecoverError.value = error instanceof Error ? error : new Error('Unknown reason');
  } finally {
    processing.value = false;
  }
}

async function validateVaultDetails() {
  onCreateError.value = undefined;
  if (!form.value?.checkValidity()) {
    onCreateError.value = new FormValidationFailedError();
    return;
  }
  if (props.recover) {
    await createVault();
  } else {
    goToNextState();
  }
}

function validateAutomaticAccessGrant() {
  onCreateError.value = undefined;
  goToNextState();
}

async function validateVaultEmergencyAccess() {
  onCreateError.value = undefined;
  if (!emergencyAccessSetup.value) {
    onCreateError.value = new Error('Invalid state.');
    return;
  }

  processing.value = true;
  try {
    let recoveryKeyProducer: RecoveryKeyProducing;
    switch (vaultType.value) {
      case VaultType.VaultFormat8: {
        if (!vaultFormat8.value) {
          throw new Error('Invalid state');
        }
        recoveryKeyProducer = vaultFormat8.value;
        break;
      }
      case VaultType.UniversalVaultFormat: {
        if (!uvfVault.value) {
          throw new Error('Invalid state');
        }
        recoveryKeyProducer = uvfVault.value;
        break;
      }
      default:
        throw new Error('Invalid state');
    }

    const { requiredKeyShares, keyShares } = await emergencyAccessSetup.value.split(vault.value.id, recoveryKeyProducer);
    vault.value.requiredEmergencyKeyShares = requiredKeyShares;
    vault.value.emergencyKeyShares = { ...keyShares };
    state.value = State.ShowRecoveryKey;
  } catch (error) {
    console.error('Validating emergency access settings failed.', error);
    onCreateError.value = error instanceof Error ? error : new Error('Unexpected error');
  } finally {
    processing.value = false;
  }
}

function goToNextState() {
  const steps = getCurrentStates.value;
  const idx = steps.indexOf(state.value);
  if (idx >= 0 && idx < steps.length - 1) {
    state.value = steps[idx + 1];
  }
}

function goToPreviousState() {
  const steps = getCurrentStates.value;
  const idx = steps.indexOf(state.value);
  if (idx > 0) {
    state.value = steps[idx - 1];
  }
}

async function createVault() {
  onCreateError.value = undefined;
  try {
    processing.value = true;
    const owner = await userdata.me;
    if (!owner.setupCode) {
      throw new Error('User not set up');
    }
    const ownerGrant: AccessGrant = { userId: owner.id, token: '' };
    switch (vaultType.value) {
      case VaultType.VaultFormat8: {
        if (!vaultFormat8.value) {
          throw new Error('Invalid state');
        }
        ownerGrant.token = await vaultFormat8.value.encryptForUser(await userdata.ecdhPublicKey);
        break;
      }
      case VaultType.UniversalVaultFormat: {
        if (!uvfVault.value) {
          throw new Error('Invalid state');
        }
        ownerGrant.token = await uvfVault.value.encryptForUser(await userdata.ecdhPublicKey, true);
        if (!props.recover) {
          // Apply the (possibly per-vault-overridden) automatic access grant policy now that the override step is done.
          // On recovery the existing vault's policy must be preserved, so we leave the recovered metadata untouched.
          uvfVault.value.metadata.automaticAccessGrant = { enabled: vaultAutoGrantEnabled.value, trustThreshold: Number(vaultAutoGrantTrustThreshold.value) };
        }
        const recoveryPublicKey = await uvfVault.value.recoveryKey.serializePublicKey();
        vault.value.uvfMetadataFile = await uvfVault.value.createMetadataFile(absBackendBaseURL, vault.value);
        vault.value.uvfKeySet = `{"keys": [${recoveryPublicKey}]}`;
        break;
      }
    }
    await backend.vaults.createOrUpdateVault(vault.value);
    await backend.vaults.grantAccess(vault.value.id, ownerGrant);
    state.value = State.Finished;
  } catch (error) {
    console.error('Creating vault failed.', error);
    onCreateError.value = error instanceof Error ? error : new Error('Unknown reason');
  } finally {
    processing.value = false;
  }
}

async function copyRecoveryKey() {
  await navigator.clipboard.writeText(recoveryKeyStr.value);
  copiedRecoveryKey.value = true;
  debouncedCopyFinish();
}

async function downloadVaultTemplate() {
  if (!vaultFormat8.value && !uvfVault.value) {
    throw new Error('Invalid state');
  }
  onDownloadTemplateError.value = undefined;
  try {
    const templateProducer: VaultTemplateProducing = vaultFormat8.value || uvfVault.value!;
    const blob = await templateProducer.exportTemplate(absBackendBaseURL, vault.value);
    if (blob != null) {
      saveAs(blob, `${vault.value.name}.zip`);
    } else {
      throw new EmptyVaultTemplateError();
    }
  } catch (error) {
    console.error('Exporting vault template failed.', error);
    onDownloadTemplateError.value = error instanceof Error ? error : new Error('Unknown reason');
  }
}
</script>
